/**
 * lib/views/javascripts/head/push.js — the push bootstrap: register the
 * service worker, mint an FCM token, hand it to POST /api/push/token; and the
 * foreground renderer, and therefore the single source of every chat banner.
 * Written by lib/views/htmls/push/bootstrap.ejs, which the desktop shell
 * (shell/body.ejs, first) and the phone shell (mobile/shell.ejs, last) both
 * include.
 *
 * Was the inline script lib/push-notification/client.js built with the web
 * config pasted into its code (ADR 0082 P3, lv-13). The config, the VAPID
 * key, the worker's path and scope, auto, and the URL of the Firebase SDK
 * arrive as the #m33-push-boot block written just before this script, which
 * reads its own block. The SDK (10.14.1 compat) comes from npm through the
 * build (ADR 0082 D5), its own module entry (lib/views/javascripts/
 * push-firebase.js), imported only once permission is granted; it was two
 * <script>s from gstatic. A head entry, loaded with a blocking <script src>
 * where the inline script stood (ADR 0082 D3), so its first-gesture listener
 * still registers when it did: on the desktop, ahead of the shell's own
 * capture handlers.
 *
 * FIVE THINGS THAT ARE EASY TO GET WRONG AND ARE HANDLED HERE.
 *
 * 1. THE PERMISSION PROMPT IS BROWSER-DEPENDENT, SO BOTH PATHS EXIST. Chrome
 *    and Edge allow Notification.requestPermission() outside a user gesture, so
 *    there we ask on load and the visitor never has to discover a button.
 *    Firefox (72+) and Safari REJECT that call with NotAllowedError unless it
 *    runs inside a real gesture — permission simply stays "default" — so we
 *    then arm a one-shot capture listener on the first click or keydown,
 *    matching what packages/@agents/relay/view/trunk.js already does. One code
 *    path, best available behaviour per browser. A user who has already granted
 *    permission skips all of it and re-registers immediately (see note 2).
 *
 * 2. TOKENS ROTATE, SO WE RE-REGISTER ON EVERY LOAD. FCM reissues a
 *    registration token when site data is cleared, the SW is replaced, or the
 *    app is reinstalled. getToken() returns the current one and the server
 *    upsert is idempotent, so calling this on every page load is how a stale
 *    row is kept from becoming a silent delivery hole. It is not wasted work.
 *
 * 3. iOS ONLY DELIVERS TO AN INSTALLED PWA. Safari 16.4+ supports Web Push,
 *    but exclusively when the site has been added to the Home Screen, and
 *    there is no API to ask whether it has been. navigator.standalone is the
 *    closest signal available, so on a non-standalone iOS device we do not
 *    prompt at all — a permission dialog there yields a token that can never
 *    receive anything, which is worse than staying silent because it makes the
 *    user believe notifications are on. The install prompt in the phone shell
 *    is the path forward for those users.
 *
 * 4. THE SW IS NOT THE ONLY RENDERER, BECAUSE IT CANNOT BE. The SDK's push
 *    listener suppresses its banner whenever ANY window client on the origin is
 *    visible — matched by ORIGIN, not by our narrow /push/ scope — so a tab that
 *    is open but unfocused receives the message and shows nothing. render()
 *    covers exactly that case, which is what lets trunk.js stop producing
 *    banners of its own and makes FCM the single source of every chat banner.
 *
 * 5. THE BACKGROUND CLICK COMES BACK HERE TOO. The service worker owns the
 *    notificationclick event, but its scope is /push/ so it controls no pages
 *    and WindowClient.navigate() would reject — it can focus a tab, not route
 *    it. So it posts { m33: 'push-click', link, channelId } to the tab it
 *    focused and openTarget() below does the routing, which is the same
 *    routing the foreground banner's own click uses. One notion of "open this
 *    channel", one implementation (rule 11).
 *
 * Every failure is a console.warn and a return. A browser that cannot do push
 * must degrade to exactly today's behaviour (polled alerts bell + foreground
 * banners), never to a broken page.
 *
 * Contents: view
 * Ontology: Code object M33C-0962 (immutable ref — assigned once, never edit)
 * Team: J. Brant Arseneau & the Mach33 Agents.
 */

// ── Boot data ────────────────────────────────────────────────────────────
// The block written just before this script (push/bootstrap.ejs).
function readBoot() {
  const script = document.currentScript;
  const block = script && script.previousElementSibling;
  if (!block || block.id !== 'm33-push-boot') {
    console.error('[push] no #m33-push-boot block before the script');
    return null;
  }
  try {
    return JSON.parse(block.textContent);
  } catch (e) {
    console.error('[push] the boot block is not valid JSON:', String(e && e.message).slice(0, 120));
    return null;
  }
}

// ── Start ────────────────────────────────────────────────────────────────
(function () {
"use strict";
var BOOT = readBoot();
if (!BOOT) return;
if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
var CFG = BOOT.cfg;
var VAPID = BOOT.vapid;
var SW_PATH = BOOT.swPath;
var SW_SCOPE = BOOT.swScope;
var busy = false, done = false;

// iOS delivers push only to a home-screen install. Detect the inverse and
// stay quiet rather than mint a token that can never be delivered to.
function iosBlocked() {
  var ua = navigator.userAgent || "";
  var isIOS = /iPhone|iPad|iPod/.test(ua) ||
              (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  var standalone = (navigator.standalone === true) ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  return !standalone;
}

// THE FOREGROUND RENDERER. The SW renders only when no window client on
// the origin is visible (the SDK's own push listener calls
// clients.matchAll({includeUncontrolled:true}) and bails if any client is
// visible — that match is by ORIGIN, not by our narrow /push/ scope). So a
// visible-but-unfocused tab got NOTHING from FCM, which is why a second
// producer had to exist in trunk.js. This closes that hole: FCM is now the
// one source of every chat banner, and trunk.js only renders when push is
// unavailable.
//
// Rendered with new Notification(), NOT reg.showNotification(). A persistent
// notification's click fires notificationclick in the SERVICE WORKER, and
// routing it from there costs a round trip through postMessage to get back
// to this same page — which is exactly what the background lane has to do
// because it has no page to start from. A foreground click already HAS one.
// (The SW's handler does read a bare data.link, so such a banner would work
// now where it used to have a dead click; it is simply the longer way
// round.)
//
// The two renderers cannot collide on a tag: the SW fires only when no
// client is visible, this fires only when one is. Mutually exclusive.
//
// Known gap: iOS Safari in a home-screen PWA has no page-level Notification
// constructor, so there is no FOREGROUND banner there. Background still
// works through the SW, which is the case that matters on a phone.
// THE ONE PLACE THAT DECIDES WHAT "OPEN THIS" MEANS, for both lanes: a
// foreground banner clicked on this page, and a background banner whose
// click the SW handed back over postMessage.
//
// In-place beats a reload. Reloading the chat SPA to reach a channel the
// page can already open is a second of blank screen for nothing, and
// trunk.js publishes its own switcher for exactly this (rule 11 — the
// switcher owns "show this channel", we do not re-derive it). Its absence
// is the honest signal that this page is not the team surface, and then a
// real navigation is correct.
//
// A msg= link always navigates: scrolling to the exact message is part of
// the ?channel=&msg= load sequence in trunk.js, not part of the switcher.
function openTarget(link, channelId) {
  try {
    try { window.focus(); } catch (_) {}
    var hasMsg = !!link && link.indexOf("msg=") !== -1;
    if (channelId && !hasMsg && typeof window.__RELAY_SWITCH_CHANNEL__ === "function") {
      window.__RELAY_SWITCH_CHANNEL__(channelId);
      return;
    }
    if (link) window.location.assign(link);
  } catch (e) { console.warn("[push] open failed: " + ((e && e.message) || e)); }
}

// Bound at load, unconditionally — NOT inside enable(). The tab the SW
// focuses is whichever one is open, which is very often not the tab that
// minted the token, and a tab that never reached getToken() would otherwise
// swallow the click in silence. Costs one listener on a browser that never
// grants permission.
try {
  navigator.serviceWorker.addEventListener("message", function (ev) {
    var m = ev && ev.data;
    if (!m || m.m33 !== "push-click") return;
    openTarget(m.link, m.channelId);
  });
} catch (_) {}

function render(payload) {
  try {
    var d = (payload && payload.data) || {};
    var n = (payload && payload.notification) || {};
    var ch = d.channelId || "";
    // The channel being read needs no banner — the message is already on
    // screen. trunk.js publishes what it is attending to; we ask.
    if (ch && window.__M33Attending && window.__M33Attending() === ch) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    var title = n.title || d.title || "Relay";
    var link = d.link || "";
    var note = new Notification(title, {
      body: String(n.body || d.body || "").slice(0, 140),
      // Same tag grammar as message.js tagFor() so a channel collapses to one
      // banner rather than stacking one per message.
      tag: d.tag || ("m33-tc-" + (ch || "msg")),
      renotify: true,
      requireInteraction: d.requireInteraction === "1" || d.requireInteraction === true
    });
    note.addEventListener("click", function () {
      openTarget(link, ch);
      try { note.close(); } catch (_) {}
    });
  } catch (e) { console.warn("[push] render failed: " + ((e && e.message) || e)); }
}

// The SDK, from the build: its own module entry, so it is fetched only here,
// once permission is granted, and never blocks a page's parse.
function loadFirebase() {
  return import(BOOT.firebase).then(function (m) { return m.default; });
}

// Returns a promise so the gesture handler can re-arm if the user dismissed
// the prompt without answering it (see arm() below).
function enable() {
  if (busy || done) return Promise.resolve();
  if (iosBlocked()) { console.info("[push] iOS needs Add to Home Screen first"); return Promise.resolve(); }
  busy = true;
  // requestPermission() is called inside a then() rather than directly, and
  // that is load-bearing on Safari. Safari THROWS NotAllowedError
  // SYNCHRONOUSLY when there is no user gesture ("Notification prompting can
  // only be done from a user gesture") instead of returning a rejected
  // promise. Called directly, that exception escapes before .catch() and
  // .finally() are attached, so two things break at once: the caller's .then()
  // never runs and the gesture fallback is never armed, and `busy` stays true
  // forever, making every later gesture a no-op. Push would be permanently
  // dead on the page. Starting from Promise.resolve() turns the synchronous
  // throw into a rejection this chain can actually see.
  return Promise.resolve().then(function () {
    // NEVER ask again when the answer is already on file. Safari throws
    // NotAllowedError for ANY gesture-less requestPermission() call — including
    // one whose answer is already "granted" — so asking redundantly stranded
    // every already-permitted Safari visitor before getToken() and left them
    // with no registration at all. Nothing prompts here; we just report what is
    // already true and let the chain continue to token minting.
    if (Notification.permission === "granted") return "granted";
    return Notification.requestPermission();
  }).then(function (perm) {
    if (perm !== "granted") { busy = false; return; }
    var firebase;
    return loadFirebase()
      .then(function (fb) { firebase = fb; })
      // Register at the narrow scope so this worker controls no pages.
      .then(function(){ return navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE }); })
      .then(function (reg) {
        if (!firebase.apps.length) firebase.initializeApp(CFG);
        var messaging = firebase.messaging();
        return messaging.getToken({ vapidKey: VAPID, serviceWorkerRegistration: reg })
          .then(function (token) {
            if (!token) throw new Error("no_token");
            return fetch("/api/push/token", {
              method: "POST", credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: token, platform: "web" })
            });
          })
          // The SDK fires onMessage INSTEAD of showing a banner while any client is
          // visible, so this is the only place a foreground banner can come from.
          // __M33Push.ready is the signal trunk.js reads to stand down (see render()).
          .then(function () {
            done = true;
            window.__M33Push = { ready: true };
            try { messaging.onMessage(render); } catch (_) {}
          });
      });
  }).catch(function (e) {
    // A gesture-less prompt on Safari/Firefox is EXPECTED, not a failure: the
    // caller re-arms for the first click and the visitor is none the wiser.
    // Logging it as a warning would put a red herring in the console of every
    // Safari session and train us to ignore the tag that does matter (rule 14).
    var msg = (e && e.message) || String(e);
    if (e && (e.name === "NotAllowedError" || /user gesture/i.test(msg))) {
      console.info("[push] prompt needs a user gesture here — waiting for one");
    } else {
      console.warn("[push] enable failed: " + msg);
    }
  }).finally(function () { busy = false; });
}

// Exposed so a settings toggle or an onboarding step can call it directly.
window.__m33PushEnable = enable;
// Arm one gesture. The listener is one-shot so a granted permission never
// pays for it again — but if the user DISMISSED the prompt (permission stays
// "default", which is distinct from "denied") we re-arm for the next
// gesture. Without that, one stray click early in the page life silently
// costs the visitor push for the whole session, and the only cure is a
// reload they have no reason to try.
// Bound on WINDOW, not document, and in the capture phase. The capture path
// is window → document → … , so this runs before any document-level handler
// — including the shell's, which calls stopImmediatePropagation() on some
// clicks. A document-level listener here loses those clicks entirely.
function arm() {
  var once = function () {
    window.removeEventListener("click", once, true);
    window.removeEventListener("keydown", once, true);
    enable().then(function () {
      if (!done && Notification.permission === "default") arm();
    });
  };
  window.addEventListener("click", once, true);
  window.addEventListener("keydown", once, true);
}

// opts.auto === false on the server attaches window.__m33PushEnable() without
// arming anything, for surfaces that want an explicit opt-in button.
if (BOOT.auto) {
  // Already granted: no gesture needed, so refresh the token straight away
  // (see note 2 — rotation makes this a per-load requirement).
  if (Notification.permission === "granted") enable();
  // TRY ON LOAD, FALL BACK TO A GESTURE. Chrome and Edge allow
  // requestPermission() outside a gesture, so the visitor gets the prompt the
  // moment they land. Firefox (since 72) and Safari reject with
  // NotAllowedError — permission stays "default", and the .then below arms the
  // click path for them. One code path, best available behaviour per browser.
  //
  // The tradeoff being made knowingly: prompting on load is the biggest driver
  // of a permanent "deny", and a deny is terminal until the user digs into
  // browser settings. Gating on a gesture converts more of those into grants.
  else if (Notification.permission === "default") {
    enable().then(function () {
      if (!done && Notification.permission === "default") arm();
    });
  }
  // "denied" is terminal until the user changes it in browser settings —
  // never prompt again, that is what gets an origin permanently blocked.
}
})();
